import { AppEvent } from "@lib/titan2/dist/game/Constants";
import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { AbstractGameView } from "../view/abstract/display/AbstractGameView";
import { SlingoEvent } from "./event/SlingoEvent";
/*
 * PayTableManager.ts
 * Manager for PayTableViews, controls landscape, portrait and dialog tables
 */
export class PayTableManager extends AbstractGameView {
    create() {
        super.create();
        this.allViews = [];
        this.createPayTableViews();
        this.initializeMouseBlocker();
    }
    createPayTableViews() {
        this.createLandscapePayTableView();
        this.createMiniPayTableView();
        this.createDialogPayTableView();
        this.allViews.forEach((paytable) => {
            paytable.updateActivePrize(this.model.getCurrentMatchedPatterns());
        });
        this.sortChildren();
    }
    initializeMouseBlocker() {
        this.mouseBlocker = new MouseBlocker(this.landscapeProperties.mouseBlockerProperties, this.portraitProperties.mouseBlockerProperties);
        this.mouseBlocker.onClick(this.handleBlockerClicked, this);
        this.addChild(this.mouseBlocker);
        this.mouseBlocker.zIndex = -1;
        this.sortChildren();
        this.mouseBlocker.hide();
    }
    handleBlockerClicked() {
        this.dispatchGame(SlingoEvent.PAY_TABLE_CLOSE);
    }
    createLandscapePayTableView() {
        this.landscapeView = this.factory.createPayTable();
        this.addPayTableView(this.landscapeView);
        this.landscapeView.setVisible(this.renderer.isLandscape());
    }
    createMiniPayTableView() {
        this.portraitView = this.factory.createMiniPayTable();
        this.addPortraitClick();
        this.addPayTableView(this.portraitView);
        this.portraitView.setVisible(this.renderer.isPortrait());
    }
    addPortraitClick() {
        this.portraitView.addOnClick(() => {
            this.dispatchGame(SlingoEvent.PAY_TABLE_OPEN);
        });
    }
    createDialogPayTableView() {
        this.dialogPayTable = this.factory.createPayTable();
        this.addDialogDismiss();
        this.addPayTableView(this.dialogPayTable);
        this.dialogPayTable.setVisible(false);
    }
    addDialogDismiss() {
        this.dialogPayTable.addOnDismiss(() => {
            this.dispatchGame(SlingoEvent.PAY_TABLE_CLOSE);
        });
    }
    addPayTableView(view) {
        this.allViews.push(view);
        this.addChild(view);
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.mouseBlocker);
        this.mouseBlocker = null;
        this.destroyViews();
    }
    destroyViews() {
        this.allViews.forEach((view) => DestroyUtil.destroy(view));
        this.allViews = [];
        this.landscapeView = null;
        this.portraitView = null;
        this.dialogPayTable = null;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.STAKE_UPDATED, this.handleStakeUpdated);
        this.registerGame(SlingoEvent.SLINGO_PAY_TABLE, this.handleSlingoPayTable);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_CLEAR, this.handleAvailableWinClear);
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
        this.registerGame(CommonGameEvent.COLLECT_REQUEST, this.handleCollectRequest);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
        this.registerCore(ResizeEvent.ROTATE, this.handleRotate);
        // portrait-relevant
        this.registerGame(SlingoEvent.PAY_TABLE_OPEN, this.handlePayTableDialogOpen);
        this.registerGame(SlingoEvent.PAY_TABLE_CLOSE, this.handlePayTableDialogClosed);
        this.registerCore(AppEvent.IDLE, this.handleIdle);
        this.registerCore(AppEvent.BUSY, this.handleBusy);
    }
    handleStandardSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handleFreeSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseEntry() {
        this.allViews.forEach((paytable) => {
            paytable.clearHighlight();
            paytable.updateActivePrize(this.model.getCurrentMatchedPatterns());
        });
        this.updatePrizeValues();
    }
    updatePrizeValues() {
        const payoutConfig = this.model.getPayoutConfig();
        const currentStake = this.model.getCurrentStake();
        const payouts = payoutConfig.getPatternPayouts();
        payouts.reverse();
        payouts.splice(1, 1);
        let payLadderValues = [];
        payouts.forEach((payout) => {
            payLadderValues.push(currentStake * payout);
        });
        this.allViews.forEach((paytable) => {
            paytable.updatePayLadderValues(payLadderValues);
            // update scatter value if displayed
            if (paytable.hasScatterText()) {
                let symbolPayouts = payoutConfig.getSymbolPayouts();
                let symbolMultipliers = symbolPayouts.getValue(undefined).getMultipliers();
                let scatterValueText = this.formatCurrency(currentStake * symbolMultipliers[5]);
                paytable.updateScatterValueText(scatterValueText);
            }
        });
        this.dispatchGame(SlingoEvent.PAY_TABLE_UPDATE);
    }
    handleStakeUpdated() {
        this.updatePrizeValues();
    }
    handleSlingoPayTable() {
        let slingosAwarded = this.model.getSlingosAwarded();
        let matchedPatterns = this.model.getCurrentMatchedPatterns();
        let patternUpdates = [];
        for (let index = slingosAwarded - 1; index >= 0; index--) {
            patternUpdates.push(matchedPatterns - index);
        }
        this.allViews.forEach((paytable, index) => {
            // callbacks are only relevant to landscape and portrait primary paytables
            if (paytable === this.landscapeView || paytable === this.portraitView) {
                paytable.updateMatchedPatterns(patternUpdates, this.handlePatternMatchUpdateProgress, this.handlePatternMatchUpdateComplete);
            }
            else {
                paytable.updateMatchedPatterns(patternUpdates);
            }
        });
    }
    handlePatternMatchUpdateProgress(view, numberOfPatterns) {
        if (view.visible) {
            this.dispatchGame(SlingoEvent.getPatternEvent(numberOfPatterns));
        }
    }
    handlePatternMatchUpdateComplete(view) {
        if (!view.visible) {
            return;
        }
        Logger.debug("PayTable: handleMatchedPatternsComplete()");
        if (this.model.getCurrentMatchedPatterns() == this.model.getGameProgress().getMatchedPatterns()) {
            //all dabs complete, ready for next spin
            this.dispatchGame(SlingoEvent.SLINGO_COMPLETE);
        }
        else {
            //check for finished slingo, but still dabs to do
            const dabData = this.model.getDabData();
            if (dabData.getAutoDabIndex() < dabData.getAutoDabCells().length) {
                this.dispatchGame(SlingoEvent.SLINGO_COMPLETE);
            }
        }
    }
    handleAvailableWin() {
        let availableWin = this.model.getAvailableWin();
        let numberOfPatterns = availableWin.getNumberOfPatterns();
        this.allViews.forEach((paytable) => {
            paytable.highlightAvailableWin(numberOfPatterns);
        });
    }
    handleAvailableWinClear() {
        this.clearHighlights();
    }
    handleSpinRequest() {
        if (this.model.isPurchaseSpin()) {
            this.clearHighlights();
        }
    }
    handleCollectRequest() {
        this.clearHighlights();
    }
    clearHighlights() {
        this.allViews.forEach((paytable) => {
            paytable.clearHighlight();
        });
    }
    handlePayTableDialogOpen() {
        this.portraitView?.setVisible(false);
        this.mouseBlocker.show();
        this.dialogPayTable?.setVisible(true);
    }
    handlePayTableDialogClosed() {
        this.mouseBlocker.hide();
        this.dialogPayTable?.setVisible(false);
        this.portraitView?.setVisible(this.renderer.isPortrait()); // show portrait paytable only if still portrait
    }
    handleBusy() {
        this.portraitView?.setInteractive(false);
    }
    handleIdle() {
        this.portraitView?.setInteractive(true);
    }
    handleRotate(event) {
        this.dispatchGame(SlingoEvent.PAY_TABLE_CLOSE);
        const data = event?.getCurrentTarget() || {};
        this.landscapeView?.setVisible(this.renderer.isLandscape(data.forceOrientation));
        this.portraitView?.setVisible(this.renderer.isPortrait(data.forceOrientation));
    }
}
//# sourceMappingURL=PayTableManager.js.map